import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs/Rx';
import { LocalStorageService } from '../services/api/local-storage.service';
import { AuthenticationProfile } from './authentication-profile.model';
import { LoginPessoa } from '../models/login-pessoa';
import { PMVAuthenticationService, AuthenticationData, DateUtils } from 'pmv-common';
import { LoginServidor } from '../models/login-servidor';
import { NumberValueAccessor } from '@angular/forms/src/directives';

/**
 * Serviço de autenticação do sistema. Controla o acesso do usuário às 
 * funcionalidades do sistema e faz o controle das
 * suas informações de acesso.
 */
@Injectable()
export class PSSAuthenticationService extends PMVAuthenticationService {

  loginPessoa: LoginPessoa = new LoginPessoa();
  loginServidor: LoginServidor = new LoginServidor();

  private authenticationSubject: Subject<boolean> = new Subject<boolean>();
  private currentUser: AuthenticationProfile;
  private readonly localStorageKey = 'pmv.authenticatedUser';

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private localStorageService: LocalStorageService
  ) {
    super();
    this.currentUser = this.localStorageService.getItem(this.localStorageKey) || null;
  }

  login(authData: AuthenticationData): Observable<boolean> {
    throw new Error("Method not implemented.");
  }
  /**
   * Faz a autenticação do usuário no sistema e salva os dados na sessão.
   *
   * @param authData dados de autenticação
   * @return um Observable que retorna um booleano indicando se a operação foi executada com sucesso
   */
  loginAuth(authData: AuthenticationData) {
    if (authData.username.length >= 11) {

      this.loginPessoa.cpf = authData.username;
      this.loginPessoa.senha = authData.password;

      this.GetLoginPessoaAuth(this.loginPessoa).subscribe(
        profile => {
          if (profile && profile.isCompletedSuccessfully) {
            this.currentUser = profile;
            this.localStorageService.setItem(this.localStorageKey, this.currentUser);
            this.authenticationSubject.next(true);
            return true;
          } else {
            return false;
          }
        });
    } else {
      this.loginServidor.matricula = authData.username;
      this.loginServidor.senha = authData.password;
      this.GetLoginServidorAuth(this.loginServidor).subscribe(
        profile => {
          if (profile && profile.isCompletedSuccessfully) {
            this.currentUser = profile;
            this.localStorageService.setItem(this.localStorageKey, this.currentUser);
            this.authenticationSubject.next(true);
            return true;
          } else {
            return false;
          }
        });
    }
  }
  /**
   * Serviço de login da pessoa.
   *
   * @param loginPessoa dados do login da pessoa
   */
  GetLoginPessoaAuth(loginPessoa: LoginPessoa): Observable<AuthenticationProfile> {
    return this.httpClient.post<AuthenticationProfile>(`/Pessoa/PostLoginPessoaAuth/`, loginPessoa);
  }
  /**
   * Serviço de login do servidor.
   *
   * @param loginServidor dados do login do servidor
   */
  GetLoginServidorAuth(loginServidor: LoginServidor): Observable<AuthenticationProfile> {
    return this.httpClient.post<AuthenticationProfile>(`/Pessoa/PostLoginServidorAuth/`, loginServidor);
  }
  /**
   * Faz o logout do usuário no sistema e limpa os dados na sessão.
   */
  logout(): void {
    this.currentUser = null;
    this.localStorageService.removeItem(this.localStorageKey);
    this.authenticationSubject.next(false);
    this.redirectToLoginHome();
  }
  /**
   * Recupear o caminho do serviço de login da pessoa no backend a ser usado na requisição.
   *
   * @return a string do serviço de login
   */
  getLoginUrl(): string {
    return '/Pessoa/PostLoginPessoaAuth/';
  }
  /**
   * Recupear o caminho do serviço de login do servidor no backend a ser usado na requisição.
   *
   * @return a string do serviço de login
   */
  getLogiServidornUrl(): string {
    return '/Pessoa/PostLoginServidorAuth/';
  }
  /**
   * Determina se o usuário está autenticado no sistema ou não.
   *
   * @return true caso o usuário esteja autenticado e false caso contrário
   */
  isUserLoggedIn(): boolean {
    return Boolean(this.getProfile());
  }

  isExperidOut(): boolean{
    if (this.currentUser === null || this.currentUser === undefined || this.currentUser.expires_in === null || this.currentUser.expires_in === undefined) {
      this.redirectToLoginPage();
      return false;
    }

    if(new Date(this.currentUser.expires_in) > new Date(Date.now())){
      return true;
    }else{
      this.redirectToLoginPage();
      return false;
    }    
  }

  /**
   * Recupera o Observable associado ao emissor de eventos de login e logout do sistema.
   *
   * @return um Observable que envia aos subscribers o status de autenticação do usuário sempre que o mesmo fizer login
   * e logout no sistema
   */
  listen(): Observable<boolean> {
    return this.authenticationSubject.asObservable();
  }
  /**
   * Recupera o perfil do usuário autenticado no sistema.
   *
   * @return o perfil do usuário autenticado no sistema ou null caso o usuário não esteja autenticado
   */
  getProfile(): AuthenticationProfile {
    //this.currentUser = this.localStorageService.getItem(this.localStorageKey) || null;

    return this.currentUser || null;
  }
  /**
   * Verifica se usuário autenticado no sistema é Admin/banca.
   *
   * @return o tipo do usuário autenticado 
   */
  getTypeAccess(): number {
    return this.currentUser ? this.currentUser.typeAccess : null;
  }
  /**
   * Recupera o tipo do usuário autenticado no sistema.
   *
   * @return o tipo do usuário autenticado ou null caso o usuário não esteja autenticado
   */
  getTypeLoged(): number {
    return this.currentUser ? this.currentUser.type : null;
  }
  /**
   * Recupera o token do usuário autenticado no sistema.
   *
   * @return o token do usuário autenticado ou null caso o usuário não esteja autenticado
   */
  getToken(): string {
    return this.currentUser ? this.currentUser.access_token : null;
  }
  /**
   * Recupera o cpf do usuário autenticado no sistema.
   *
   * @return o cpf do usuário autenticado ou null caso o usuário não esteja autenticado
   */
  getIdLoged(): number {
    return this.currentUser ? this.currentUser.id : null;
  }
  /**
   * Recupera o nome do usuário autenticado no sistema.
   *
   * @return o nome do usuário autenticado ou null caso o usuário não esteja autenticado
   */
  getName(): string {
    return this.currentUser ? this.currentUser.nome : null;
  }
  /**
   * Redireciona o usuário para a tela de login do sistema.
   */
  redirectToLoginPage(): void {
    this.router.navigate(['login']);
  }
  /**
   * Redireciona o usuário para a tela de home do sistema.
   */
  redirectToLoginHome(): void {
    this.router.navigate(['home']);
  }
}



// WEBPACK FOOTER //
// ./src/app/authentication/authentication.service.ts